import { action, makeObservable } from "mobx";

import { createNodeFrom } from "@/models/nodes/createNodeFrom";

import type { DocumentModel } from "./DocumentModel";
import type { PlaceholderNodeModel } from "@/models/nodes/PlaceholderNodeModel";
import type { NodeModel } from "@/models/nodes/types";

export class PlaceholderRegistryModel {
  placeholders = new Map<string, Set<PlaceholderNodeModel>>();

  doc: DocumentModel;

  constructor(doc: DocumentModel) {
    this.doc = doc;

    makeObservable(this, {
      placeholders: true,
      register: action,
      unregister: action,
      replacePlaceholders: action,
    });
  }

  register(placeholder: PlaceholderNodeModel): void {
    const { linkId } = placeholder;

    if (!linkId) return;

    if (!this.placeholders.has(linkId)) {
      this.placeholders.set(linkId, new Set());
    }

    this.placeholders.get(linkId)!.add(placeholder);
  }

  unregister(placeholder: PlaceholderNodeModel): void {
    const { linkId } = placeholder;

    if (!linkId) return;

    const set = this.placeholders.get(linkId);

    if (set) {
      set.delete(placeholder);

      if (set.size === 0) {
        this.placeholders.delete(linkId);
      }
    }
  }

  replacePlaceholders(
    placeholder: PlaceholderNodeModel,
    nodes: NodeModel[],
    excludePlaceholder?: PlaceholderNodeModel,
  ): void {
    const placeholdersToReplace = placeholder.linkId
      ? this.getPlaceholders(placeholder.linkId).filter((p) => p !== excludePlaceholder)
      : [];

    placeholdersToReplace.forEach((placeholderNode) => {
      const clonedNodes = this.doc.batchCreateNodes(() => {
        return nodes.map((node) => createNodeFrom(node, this.doc, { resetIds: true }));
      });

      if (clonedNodes.length > 0) {
        placeholderNode.replaceWith(clonedNodes[0]);

        let previousNode = clonedNodes[0];

        for (let i = 1; i < clonedNodes.length; i++) {
          previousNode.insertSiblingAfter(clonedNodes[i]);
          previousNode = clonedNodes[i];
        }
      }
    });
  }

  getPlaceholders(placeholderId: string): PlaceholderNodeModel[] {
    const set = this.placeholders.get(placeholderId);
    return set ? Array.from(set) : [];
  }

  dispose(): void {
    this.placeholders.clear();
  }
}
